import { useState } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { useAppUpdates, useFeedbackHelpers } from "../hooks/useFeedbackData";
import { XMarkIcon, PlusIcon, PencilIcon } from "@heroicons/react/24/outline";

const AppUpdatesPage: React.FC = () => {
    const {
        updates,
        addUpdate,
        updateUpdate,
    } = useAppUpdates();

    const { formatDate } = useFeedbackHelpers();
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [newUpdateText, setNewUpdateText] = useState("");
    const [selectedPlatform, setSelectedPlatform] = useState<'peak^' | 'coaches^'>('peak^');
    const [submittingUpdate, setSubmittingUpdate] = useState(false);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [viewingUpdate, setViewingUpdate] = useState<any | null>(null);
    const [editingUpdate, setEditingUpdate] = useState<any | null>(null);

    const handleAddUpdate = async () => {
        if (!newUpdateText.trim()) return;

        setSubmittingUpdate(true);
        setUpdateError(null);
        try {
            await addUpdate(newUpdateText.trim(), selectedPlatform);
            setUpdateDialogOpen(false);
            setNewUpdateText("");
            setSelectedPlatform('peak^');
            setUpdateError(null);
        } catch (error) {
            console.error("Error adding update:", error);
            const errorMessage =
                error instanceof Error
                ? error.message
                : "Failed to add update. Please try again.";
            setUpdateError(errorMessage);
        }
        setSubmittingUpdate(false);
    };

    const handleEditUpdate = async () => {
        if (!newUpdateText.trim() || !editingUpdate) return;

        setSubmittingUpdate(true);
        setUpdateError(null);
        try {
            await updateUpdate(editingUpdate.id, newUpdateText.trim(), selectedPlatform);
            setEditingUpdate(null);
            setNewUpdateText("");
            setSelectedPlatform('peak^');
            setUpdateError(null);
        } catch (error) {
            console.error("Error updating update:", error);
            const errorMessage =
                error instanceof Error
                ? error.message
                : "Failed to update. Please try again.";
            setUpdateError(errorMessage);
        }
        setSubmittingUpdate(false);
    };

    const openEditDialog = (item: any, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingUpdate(item);
        setNewUpdateText(item.message);
        setSelectedPlatform(item.platform);
        setUpdateError(null);
    };

    return (
        <DashboardWrapper>
            <div className="w-full flex flex-col gap-6 p-6 md:h-[calc(100vh-64px)] overflow-hidden">
                <div className="flex items-start justify-between gap-4">
                    <h1 className="text-2xl font-heading">App Updates</h1>
                    <button
                        className="btn btn-primary btn-sm !h-10 gap-2"
                        onClick={() => {
                            setUpdateError(null);
                            setUpdateDialogOpen(true);
                        }}
                        >
                        <PlusIcon className="w-4 h-4" />
                        Add Update
                    </button>
                </div>

                {/* Latest Updates Row */}
                <div className="w-full grid md:grid-cols-2 gap-2 shrink-0">
                    <div className="w-full bg-white rounded-xl flex flex-col p-4 gap-2 border">
                        <div className="font-heading">Latest update for Peak^ </div>
                        <div className="text-gray-600 whitespace-pre-line">
                            {updates.find(update => update.platform === 'peak^')?.message || 'No updates available'}
                        </div>
                    </div>
                    <div className="w-full bg-white rounded-xl flex flex-col p-4 gap-2 border">
                        <div className="font-heading">Latest update for the Coaches Corner^ </div>
                        <div className="text-gray-600 whitespace-pre-line">
                            {updates.find(update => update.platform === 'coaches^')?.message || 'Update not available'}
                        </div>
                    </div>
                </div>

                {/* Updates Table */}
                <div className="flex-1 min-h-0 bg-white rounded-xl border overflow-hidden flex flex-col">
                    {/* Table Header */}
                    <div className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[1.5fr_6fr_1fr_1fr_24px] gap-2 md:gap-4 px-4 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase shrink-0">
                        <div className="hidden md:block">Date</div>
                        <div>Message</div>
                        <div className="hidden md:block">Platform</div>
                        <div>Status</div>
                        <div className="hidden md:block"></div>
                    </div>

                    {/* Table Body */}
                    <div className="flex-1 overflow-y-auto min-h-0">
                        {updates.map((item) => {
                            const latestForPlatform = updates.find(u => u.platform === item.platform);
                            const isLatest = latestForPlatform?.id === item.id;

                            return (
                                <div
                                    key={item.id}
                                    className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[1.5fr_6fr_1fr_1fr_24px] gap-2 md:gap-4 px-4 py-3 border-b hover:bg-gray-50 items-center transition-colors cursor-pointer"
                                    onClick={() => setViewingUpdate(item)}
                                >
                                    {/* Date - Desktop only */}
                                    <div className="hidden md:block">
                                        <span className="text-sm text-gray-600">
                                            {formatDate(item.createdAt)}
                                        </span>
                                    </div>

                                    {/* Message */}
                                    <div className="flex flex-col gap-0.5 min-w-0">
                                        <span className="text-xs text-gray-400 md:hidden">
                                            {formatDate(item.createdAt)}
                                        </span>
                                        <p className="text-sm text-gray-600 line-clamp-2 whitespace-pre-line">{item.message}</p>
                                        {/* Platform badge - Mobile only */}
                                        <span className="inline-block w-fit px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-800 rounded capitalize md:hidden mt-1">
                                            {item.platform}
                                        </span>
                                    </div>

                                    {/* Platform - Desktop only */}
                                    <div className="hidden md:block">
                                        <span className={`inline-block px-2 py-1 text-xs rounded capitalize ${item.platform === 'peak^' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                                            {item.platform}
                                        </span>
                                    </div>

                                    {/* Status */}
                                    <div className="flex items-center">
                                        <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase rounded-full whitespace-nowrap ${isLatest ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                            {isLatest ? 'Latest' : 'Deprecated'}
                                        </span>
                                    </div>

                                    {/* Action */}
                                    <div className="flex items-center justify-end">
                                        {isLatest && (
                                            <button
                                                onClick={(e) => openEditDialog(item, e)}
                                                className="p-1.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700"
                                                title="Edit update"
                                            >
                                                <PencilIcon className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                {/* Add Update Dialog */}
                {updateDialogOpen && (
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black bg-opacity-50">
                        <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-heading">Add Update</h3>
                                <button onClick={() => setUpdateDialogOpen(false)} className="text-gray-400 hover:text-gray-600">
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="px-4 sm:px-6 py-4">
                                {updateError && (
                                    <div className="alert alert-error mb-4">
                                        <span>{updateError}</span>
                                    </div>
                                )}
                                <div className="form-group mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Platform
                                    </label>
                                    <select
                                        className="w-full bg-gray-200 rounded-md p-3 font-grotesk"
                                        value={selectedPlatform}
                                        onChange={(e) => setSelectedPlatform(e.target.value as 'peak^' | 'coaches^')}
                                        disabled={submittingUpdate}
                                    >
                                        <option value="peak^">Peak^</option>
                                        <option value="coaches^">Coaches Corner^</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Message
                                    </label>
                                    <textarea
                                        className="form-textarea h-96 sm:h-48 w-full bg-gray-200 rounded-md p-3 sm:p-4"
                                        value={newUpdateText}
                                        onChange={(e) => setNewUpdateText(e.target.value)}
                                        placeholder="What's new in this update?"
                                        disabled={submittingUpdate}
                                    />
                                </div>
                            </div>
                            <div className="flex justify-between gap-3 px-4 sm:px-6 py-4 border-t border-gray-200">
                                <button
                                    onClick={() => {
                                        setUpdateDialogOpen(false);
                                        setUpdateError(null);
                                    }}
                                    className="bg-gray-100 p-2 px-4 rounded-md text-sm"
                                    disabled={submittingUpdate}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddUpdate}
                                    className="bg-black text-white p-2 px-4 rounded-md text-sm"
                                    disabled={!newUpdateText.trim() || submittingUpdate}
                                >
                                    {submittingUpdate ? (
                                        <>
                                            <span className="loading loading-spinner loading-sm mr-2"></span>
                                            Adding...
                                        </>
                                    ) : (
                                        "Add Update"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Update Dialog */}
                {editingUpdate && (
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black bg-opacity-50">
                        <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-heading">Edit Update</h3>
                                <button onClick={() => {
                                    setEditingUpdate(null);
                                    setNewUpdateText("");
                                    setSelectedPlatform('peak^');
                                    setUpdateError(null);
                                }} className="text-gray-400 hover:text-gray-600">
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="px-4 sm:px-6 py-4">
                                {updateError && (
                                    <div className="alert alert-error mb-4">
                                        <span>{updateError}</span>
                                    </div>
                                )}
                                <div className="form-group mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Platform
                                    </label>
                                    <select
                                        className="w-full bg-gray-200 rounded-md p-3 font-grotesk"
                                        value={selectedPlatform}
                                        onChange={(e) => setSelectedPlatform(e.target.value as 'peak^' | 'coaches^')}
                                        disabled={submittingUpdate}
                                    >
                                        <option value="peak^">Peak^</option>
                                        <option value="coaches^">Coaches Corner^</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Message
                                    </label>
                                    <textarea
                                        className="form-textarea h-96 sm:h-48 w-full bg-gray-200 rounded-md p-3 sm:p-4"
                                        value={newUpdateText}
                                        onChange={(e) => setNewUpdateText(e.target.value)}
                                        placeholder="What's new in this update?"
                                        disabled={submittingUpdate}
                                    />
                                </div>
                            </div>
                            <div className="flex justify-between gap-3 px-4 sm:px-6 py-4 border-t border-gray-200">
                                <button
                                    onClick={() => {
                                        setEditingUpdate(null);
                                        setNewUpdateText("");
                                        setSelectedPlatform('peak^');
                                        setUpdateError(null);
                                    }}
                                    className="bg-gray-100 p-2 px-4 rounded-md text-sm"
                                    disabled={submittingUpdate}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleEditUpdate}
                                    className="bg-black text-white p-2 px-4 rounded-md text-sm"
                                    disabled={!newUpdateText.trim() || submittingUpdate}
                                >
                                    {submittingUpdate ? (
                                        <>
                                            <span className="loading loading-spinner loading-sm mr-2"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        "Save Changes"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* View Update Dialog */}
                {viewingUpdate && (() => {
                    const latestForPlatform = updates.find(u => u.platform === viewingUpdate.platform);
                    const isLatest = latestForPlatform?.id === viewingUpdate.id;
                    return (
                        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black bg-opacity-50">
                            <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                                <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200">
                                    <h3 className="text-lg font-heading">Update Details</h3>
                                    <div className="flex items-center gap-2">
                                        {!isLatest && (
                                            <span className="inline-flex px-2 py-1 text-xs capitalize rounded bg-gray-100 text-gray-600">
                                                Deprecated
                                            </span>
                                        )}
                                        <span className={`inline-block px-2 py-1 text-xs rounded capitalize ${viewingUpdate.platform === 'peak^' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                                            {viewingUpdate.platform}
                                        </span>
                                    </div>
                                </div>
                                <div className="px-4 sm:px-6 py-4">
                                    <div className="mb-4">
                                        <span className="text-gray-500 text-sm">
                                            {formatDate(viewingUpdate.createdAt)}
                                        </span>
                                    </div>
                                    <div className="text-gray-800 whitespace-pre-line">
                                        {viewingUpdate.message}
                                    </div>
                                </div>
                                <div className="flex justify-end gap-3 px-4 sm:px-6 py-4 border-t border-gray-200">
                                    <button
                                        onClick={() => setViewingUpdate(null)}
                                        className="bg-gray-100 hover:bg-gray-200 p-2 px-4 rounded-md text-sm transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}

            </div>
        </DashboardWrapper>
    );
};

export default AppUpdatesPage;
